/**
 * Local persistence for Paul conversation list + detail (stale-while-revalidate).
 */

import { storage } from "../../../utils/browser.js";

const CACHE_PREFIX = "bubakii.conversation";
const CACHE_VERSION = 1;
const MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000;

function readEntry(key) {
  const raw = storage.get(key);
  if (!raw) return null;

  try {
    const entry = JSON.parse(raw);
    if (!entry || entry.version !== CACHE_VERSION || !entry.fetchedAt) {
      return null;
    }
    if (Date.now() - entry.fetchedAt > MAX_AGE_MS) {
      storage.remove(key);
      return null;
    }
    return entry;
  } catch {
    storage.remove(key);
    return null;
  }
}

function writeEntry(key, data) {
  const entry = {
    version: CACHE_VERSION,
    fetchedAt: Date.now(),
    data,
  };
  storage.set(key, JSON.stringify(entry));
  return entry;
}

export function buildConversationDetailCacheKey(phone, leadId = null) {
  const channel = String(phone || "").trim();
  const lead = leadId ? String(leadId).trim() : "none";
  return `${CACHE_PREFIX}.detail.${channel}.${lead}`;
}

export function buildConversationListCacheKey(filters = {}) {
  const normalized = {
    search: filters.search || "",
    status: filters.status || "open",
    source: filters.source || "",
    since: filters.since || "",
    needsAttention: Boolean(filters.needsAttention),
    page: filters.page || 1,
    pageSize: filters.pageSize || 25,
  };
  return `${CACHE_PREFIX}.list.${JSON.stringify(normalized)}`;
}

export function getConversationDetailCache(phone, leadId = null) {
  if (!phone) return null;
  return readEntry(buildConversationDetailCacheKey(phone, leadId));
}

export function setConversationDetailCache(phone, leadId, data) {
  if (!phone || data == null) return null;
  return writeEntry(buildConversationDetailCacheKey(phone, leadId), data);
}

export function getConversationListCache(filters = {}) {
  return readEntry(buildConversationListCacheKey(filters));
}

export function setConversationListCache(filters, data) {
  if (data == null) return null;
  return writeEntry(buildConversationListCacheKey(filters), data);
}

export function fingerprintConversationDetail(data) {
  if (!data) return null;

  const summary = data.summary;
  const messages = summary?.fullChat?.messages;

  return JSON.stringify({
    messageCount: summary?.messageCount ?? messages?.length ?? 0,
    generatedAt:
      summary?.generatedAt ??
      summary?.metadata?.generatedAt ??
      summary?.metadata?.updatedAt ??
      null,
    textLen: summary?.text?.length ?? 0,
    transcriptLen: summary?.fullChat?.transcript?.length ?? 0,
    n8nError: data.n8nError?.code ?? null,
  });
}
